import {
  ApplicationConfig,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { provideRouter } from '@angular/router';

import { routes } from './app-routing.module';

import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';


import { MessageService } from 'primeng/api';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { TranslocoHttpLoader } from './core/i18n/transloco-loader';
import { APP_LANGS, DEFAULT_LANG, resolveInitialLang } from './core/i18n/language';


export const appConfig: ApplicationConfig = {
  providers: [
    // LOCALE_ID se fija en bootstrap con el idioma inicial (persistido/navegador).
    { provide: LOCALE_ID, useFactory: resolveInitialLang },
    provideTransloco({
      config: {
        availableLangs: [...APP_LANGS],
        defaultLang: resolveInitialLang(),
        fallbackLang: DEFAULT_LANG,
        // Durante el rollout incremental (módulos aún sin traducir), las claves
        // que falten en EN caen a la traducción en ES en vez de mostrar la clave.
        missingHandler: { useFallbackTranslation: true },
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    // Muchas pantallas arman textos con translate() al abrir (pasos del
    // registro, menús, columnas): si el archivo del idioma no había llegado,
    // mostraban la clave ("register.step1"). Se carga antes de arrancar, junto
    // con el español (el respaldo cuando falta una clave). Si falla la red, la
    // app arranca igual.
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      const lang = resolveInitialLang();
      const langs = lang === DEFAULT_LANG ? [lang] : [lang, DEFAULT_LANG];
      return firstValueFrom(forkJoin(langs.map(l => transloco.load(l)))).then(
        () => undefined,
        () => undefined,
      );
    }),
    ToastModule,
    MessageService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-theme'
        }
      }
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, errorInterceptor])
    )
  ]
};
